import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Check,
  CheckSquare,
  Copy,
  Edit,
  Eye,
  EyeOff,
  Layers,
  List,
  MoreVertical,
  Plus,
  Search,
  Square,
  Trash2
} from 'lucide-react';
import { Segment } from './types';
import { SOURCE_SEGMENT_COLOR, COMBO_SEGMENT_COLOR } from '../constants';
import { formatTime } from './helpers';
import {
  getComboDisplayTitle,
  getComboRange,
  getComboTimelineId,
  getSourceSegments,
  getSourceSegmentDisplayTitle,
  isCombo
} from './utils';
import { getInteractiveSegmentColor } from './styles';
import LoopGlyph from './LoopGlyph';
import './StudioList.css';
import RenameDialog from './RenameDialog';

export type StudioTab = 'segments' | 'combos';
type StudioListVariant = 'windowed' | 'fullscreen';

export interface StudioListProps {
  segments: Segment[];
  activeSegment: string | null;
  activeComboId: string | null;
  isLooping: boolean;
  editingSegment: string | null;
  segmentNameInputRef: React.RefObject<HTMLInputElement | null>;
  onSaveSegmentName: (segmentId: string, newName: string) => void;
  onStartEditingSegment: (segmentId: string) => void;
  onPlaySegment: (segmentId: string) => void;
  onSelectCombo: (comboId: string) => void;
  onToggleSegmentLearned: (segmentId: string) => void;
  onToggleSegmentDisabled: (segmentId: string) => void;
  onDeleteSegment: (segmentId: string) => void;
  onBatchToggleDisabledSelected: (tab: StudioTab) => void;
  onBatchDeleteSelected: (tab: StudioTab) => void;
  onCreateCombo: () => void;
  onDuplicateCombo: (comboId: string) => void;
  onDeleteCombo: (comboId: string) => void;
  activeTab: StudioTab;
  onActiveTabChange: (tab: StudioTab) => void;
  videoSrc?: string | null;
  onToggleAllLearned?: (learned: boolean, tab: StudioTab) => void;
  variant?: StudioListVariant;
  className?: string;
  showTabs?: boolean;
  headerControl?: React.ReactNode;
  /** Source segment under the playhead; the segments list follows it when no segment is looping. */
  playingSegmentId?: string | null;
  /** Closes the rename dialog without saving. */
  onCancelEditingSegment?: () => void;
}

const getComboSegments = (segments: Segment[]) => {
  return [...segments]
    .filter(isCombo)
    .sort((a, b) => a.startTime - b.startTime);
};

const getSegmentDuration = (segment: Segment) => {
  return Math.max(0, segment.endTime - segment.startTime);
};

const getStudioRowColorStyle = (color: string): React.CSSProperties => ({
  '--studio-row-color': color,
  '--studio-row-hover-color': getInteractiveSegmentColor(color)
} as React.CSSProperties);

const StudioList: React.FC<StudioListProps> = ({
  segments,
  activeSegment,
  activeComboId,
  isLooping,
  editingSegment,
  segmentNameInputRef,
  onSaveSegmentName,
  onStartEditingSegment,
  onPlaySegment,
  onSelectCombo,
  onToggleSegmentLearned,
  onToggleSegmentDisabled,
  onDeleteSegment,
  onBatchToggleDisabledSelected,
  onBatchDeleteSelected,
  onCreateCombo,
  onDuplicateCombo,
  onDeleteCombo,
  activeTab,
  onActiveTabChange,
  videoSrc = null,
  onToggleAllLearned,
  variant = 'windowed',
  className = '',
  showTabs = true,
  headerControl,
  playingSegmentId = null,
  onCancelEditingSegment
}) => {
  const [selectMode, setSelectMode] = useState(false);
  const [segmentThumbnails, setSegmentThumbnails] = useState<Record<string, string>>({});
  const previewVideoRef = useRef<HTMLVideoElement | null>(null);
  const previewCacheRef = useRef<Record<string, string>>({});
  const previewRequestRef = useRef(0);
  const listContainerRef = useRef<HTMLDivElement | null>(null);
  const segmentRowRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const sourceSegments = getSourceSegments(segments);
  const comboSegments = getComboSegments(segments);
  const activeSourceSegment = sourceSegments.find(segment => segment.id === activeSegment) ?? sourceSegments[0] ?? null;
  const activeCombo = comboSegments.find(segment => segment.id === activeComboId)
    ?? comboSegments.find(segment => segment.id === activeSegment)
    ?? comboSegments[0]
    ?? null;
  const tabRows = activeTab === 'segments' ? sourceSegments : comboSegments;
  const selectedRows = tabRows.filter(segment => Boolean(segment.isLearned));
  const selectedCount = selectedRows.length;
  const hasSelectedRows = selectedCount > 0;
  const hasEnabledSelectedRows = selectedRows.some(segment => !segment.isDisabled);
  const tabNoun = activeTab === 'segments' ? 'segment' : 'combo';
  const tabNounPlural = activeTab === 'segments' ? 'segments' : 'combos';
  const currentRowId = activeTab === 'segments' ? activeSegment : activeComboId ?? activeCombo?.id ?? null;
  const canScrollToCurrent = Boolean(currentRowId && segmentRowRefs.current[currentRowId]);
  // The row the list follows automatically: the looping segment/combo, or on the segments tab the
  // segment currently under the playhead.
  const followRowId = activeTab === 'segments'
    ? (activeSegment && sourceSegments.some(segment => segment.id === activeSegment) ? activeSegment : playingSegmentId)
    : currentRowId;
  const lastManualListScrollRef = useRef(0);

  useEffect(() => {
    const listContainer = listContainerRef.current;
    if (!listContainer) return;
    const markManualScroll = () => {
      lastManualListScrollRef.current = Date.now();
    };
    listContainer.addEventListener('wheel', markManualScroll, { passive: true });
    listContainer.addEventListener('touchstart', markManualScroll, { passive: true });
    return () => {
      listContainer.removeEventListener('wheel', markManualScroll);
      listContainer.removeEventListener('touchstart', markManualScroll);
    };
  }, []);

  // Re-run the follow when the list becomes visible (e.g. the phone panel slides open).
  const [isListVisible, setIsListVisible] = useState(false);
  useEffect(() => {
    const listContainer = listContainerRef.current;
    if (!listContainer) return;
    if (typeof ResizeObserver === 'undefined') {
      setIsListVisible(true);
      return;
    }
    const observer = new ResizeObserver(() => {
      setIsListVisible(listContainer.clientHeight > 0);
    });
    observer.observe(listContainer);
    return () => observer.disconnect();
  }, []);

  // Scrolls only the list itself (never the page) so the followed row is in view.
  useEffect(() => {
    if (!followRowId || !isListVisible) return;
    if (Date.now() - lastManualListScrollRef.current < 3000) return;
    const row = segmentRowRefs.current[followRowId];
    const listContainer = listContainerRef.current;
    if (!row || !listContainer || listContainer.scrollHeight <= listContainer.clientHeight) return;

    const containerBounds = listContainer.getBoundingClientRect();
    const rowBounds = row.getBoundingClientRect();
    if (rowBounds.top >= containerBounds.top && rowBounds.bottom <= containerBounds.bottom) return;

    const top = listContainer.scrollTop
      + (rowBounds.top - containerBounds.top)
      - (containerBounds.height - rowBounds.height) / 2;
    if (typeof listContainer.scrollTo === 'function') {
      listContainer.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    } else {
      listContainer.scrollTop = Math.max(0, top);
    }
  }, [followRowId, activeTab, isListVisible]);

  useEffect(() => {
    previewRequestRef.current += 1;
    previewCacheRef.current = {};
    setSegmentThumbnails({});

    if (!videoSrc) {
      previewVideoRef.current = null;
      return;
    }

    const previewVideo = document.createElement('video');
    previewVideo.src = videoSrc;
    previewVideo.preload = 'auto';
    previewVideo.muted = true;
    previewVideo.playsInline = true;
    previewVideo.crossOrigin = 'anonymous';
    previewVideoRef.current = previewVideo;

    return () => {
      previewVideoRef.current = null;
      previewVideo.pause();
      previewVideo.removeAttribute('src');
      previewVideo.load();
    };
  }, [videoSrc]);

  const capturePreviewFrame = useCallback(async (segmentId: string, requestedTime: number): Promise<string | null> => {
    const previewVideo = previewVideoRef.current;
    if (!previewVideo || !videoSrc) return null;

    const cacheKey = `${segmentId}-${requestedTime.toFixed(2)}`;
    if (previewCacheRef.current[cacheKey]) {
      return previewCacheRef.current[cacheKey];
    }

    const waitForVideoEvent = (eventName: 'loadedmetadata' | 'seeked') => {
      return new Promise<void>((resolve, reject) => {
        const handleSuccess = () => {
          cleanup();
          resolve();
        };
        const handleError = () => {
          cleanup();
          reject(new Error(`Preview video ${eventName} failed`));
        };
        const cleanup = () => {
          previewVideo.removeEventListener(eventName, handleSuccess);
          previewVideo.removeEventListener('error', handleError);
        };

        previewVideo.addEventListener(eventName, handleSuccess, { once: true });
        previewVideo.addEventListener('error', handleError, { once: true });
      });
    };

    try {
      if (previewVideo.readyState < 1) {
        await waitForVideoEvent('loadedmetadata');
      }

      const sourceDuration = Number.isFinite(previewVideo.duration) ? previewVideo.duration : 0;
      const maxAllowedTime = Math.max(sourceDuration - 0.05, 0);
      const targetTime = Math.min(Math.max(requestedTime, 0), maxAllowedTime);
      previewVideo.currentTime = targetTime;
      await waitForVideoEvent('seeked');

      const sourceWidth = previewVideo.videoWidth || 160;
      const sourceHeight = previewVideo.videoHeight || 90;
      const maxWidth = 160;
      const maxHeight = 90;
      const scale = Math.min(maxWidth / sourceWidth, maxHeight / sourceHeight, 1);
      const frameWidth = Math.max(1, Math.round(sourceWidth * scale));
      const frameHeight = Math.max(1, Math.round(sourceHeight * scale));

      const canvas = document.createElement('canvas');
      canvas.width = frameWidth;
      canvas.height = frameHeight;
      const context = canvas.getContext('2d');
      if (!context) return null;

      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      context.drawImage(previewVideo, 0, 0, frameWidth, frameHeight);
      const previewDataUrl = canvas.toDataURL('image/jpeg', 0.78);
      previewCacheRef.current[cacheKey] = previewDataUrl;
      return previewDataUrl;
    } catch {
      return null;
    }
  }, [videoSrc]);

  useEffect(() => {
    if (!videoSrc || segments.length === 0) return;

    let isCancelled = false;
    const requestId = previewRequestRef.current + 1;
    previewRequestRef.current = requestId;

    const loadSegmentThumbnails = async () => {
      const previewSegments = [...getSourceSegments(segments), ...getComboSegments(segments)];
      const nextThumbnails: Record<string, string> = {};

      for (const segment of previewSegments) {
        const previewTime = Math.max(segment.startTime + 0.05, 0);
        const thumbnail = await capturePreviewFrame(segment.id, previewTime);
        if (isCancelled || previewRequestRef.current !== requestId) return;
        if (thumbnail) {
          nextThumbnails[segment.id] = thumbnail;
        }
      }

      if (!isCancelled && Object.keys(nextThumbnails).length > 0) {
        setSegmentThumbnails(prev => ({ ...prev, ...nextThumbnails }));
      }
    };

    void loadSegmentThumbnails();
    return () => {
      isCancelled = true;
    };
  }, [capturePreviewFrame, segments, videoSrc]);

  // A hidden row can't be played, so its "Off" tag doubles as the way to bring it back.
  const renderShowHiddenButton = (rowId: string, rowTitle: string) => (
    <button
      type="button"
      className="studio-row-badge studio-row-show-button"
      onClick={event => {
        event.stopPropagation();
        onToggleSegmentDisabled(rowId);
      }}
      title={`${rowTitle} is hidden. Click to show it again.`}
      aria-label={`Show ${rowTitle} again`}
    >
      <Eye size={11} aria-hidden="true" />
      Show
    </button>
  );

  const handleRowKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    action: () => void,
    disabled = false
  ) => {
    if (disabled) return;
    // Keys typed into a field inside the row (e.g. renaming) belong to that field, so Space still
    // types a space instead of activating the row.
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  };

  const scrollToCurrentRow = () => {
    if (!currentRowId) return;
    const activeRow = segmentRowRefs.current[currentRowId];
    const listContainer = listContainerRef.current;
    if (!activeRow || !listContainer) return;

    const containerBounds = listContainer.getBoundingClientRect();
    const rowBounds = activeRow.getBoundingClientRect();
    const isOutsideViewport = rowBounds.top < containerBounds.top || rowBounds.bottom > containerBounds.bottom;
    activeRow.scrollIntoView({
      block: isOutsideViewport ? 'center' : 'nearest',
      behavior: 'smooth'
    });
  };

  const confirmDeleteSegment = (segment: Segment) => {
    if (window.confirm(`Delete "${segment.name}"? This cannot be undone.`)) {
      onDeleteSegment(segment.id);
    }
  };

  const confirmDeleteCombo = (combo: Segment) => {
    if (window.confirm(`Delete "${combo.name}"? This cannot be undone.`)) {
      onDeleteCombo(combo.id);
    }
  };

  const confirmBatchDelete = () => {
    if (selectedCount === 0) return;
    const noun = selectedCount === 1 ? tabNoun : tabNounPlural;
    if (window.confirm(`Delete ${selectedCount} selected ${noun}? This cannot be undone.`)) {
      onBatchDeleteSelected(activeTab);
      setSelectMode(false);
    }
  };

  const renderName = (
    segment: Segment,
    ariaLabel: string,
    displayName = segment.name,
    fullName = segment.name,
    numeric = false
  ) => {
    // Renaming happens in a full-screen dialog (see below), so the row always shows the name.
    return (
      <span
        className={`studio-row-name ${numeric ? 'studio-row-name-number' : ''}`}
        title={fullName}
        aria-label={fullName}
        data-rename-label={ariaLabel}
      >
        {displayName}
      </span>
    );
  };

  const editingRow = editingSegment
    ? segments.find(segment => segment.id === editingSegment) ?? null
    : null;

  const renderThumbnail = (segment: Segment, indexLabel: string) => (
    <span className="studio-row-thumb" aria-hidden="true">
      {segmentThumbnails[segment.id] ? (
        <img src={segmentThumbnails[segment.id]} alt="" />
      ) : (
        <span className="studio-row-thumb-fallback" />
      )}
      <span className="studio-row-index">{indexLabel}</span>
    </span>
  );

  const renderSegmentRow = (segment: Segment, index: number) => {
    const stableSegment = { ...segment, color: SOURCE_SEGMENT_COLOR };
    const isActive = activeSegment === segment.id;
    const isSelected = Boolean(segment.isLearned);
    const isDisabled = Boolean(segment.isDisabled);
    const isPlaying = isLooping && isActive;
    const segmentTimelineId = String(index + 1);
    const segmentDisplayTitle = getSourceSegmentDisplayTitle(segment, segmentTimelineId);
    const interactiveColor = getInteractiveSegmentColor(SOURCE_SEGMENT_COLOR);
    const rowAction = () => {
      if (selectMode) {
        onToggleSegmentLearned(segment.id);
        return;
      }
      if (!isDisabled) {
        onPlaySegment(segment.id);
      }
    };

    return (
      <div
        key={segment.id}
        ref={element => {
          segmentRowRefs.current[segment.id] = element;
        }}
        role="button"
        tabIndex={isDisabled && !selectMode ? -1 : 0}
        data-testid={`studio-segment-row-${segment.id}`}
        className={[
          'studio-row',
          isActive ? 'is-active' : '',
          isSelected ? 'is-selected' : '',
          isDisabled ? 'is-disabled' : '',
          isPlaying ? 'is-playing' : ''
        ].filter(Boolean).join(' ')}
        style={getStudioRowColorStyle(SOURCE_SEGMENT_COLOR)}
        onClick={rowAction}
        onKeyDown={event => handleRowKeyDown(event, rowAction, isDisabled && !selectMode)}
        title={segmentDisplayTitle}
      >
        {renderThumbnail(stableSegment, String(index + 1))}
        <span className="studio-row-copy">
          <span className="studio-row-main">
            {selectMode && (
              <button
                type="button"
                className={`studio-row-check ${isSelected ? 'is-selected' : ''}`}
                onClick={event => {
                  event.stopPropagation();
                  onToggleSegmentLearned(segment.id);
                }}
                aria-label={isSelected ? `Deselect ${segmentDisplayTitle}` : `Select ${segmentDisplayTitle}`}
              >
                <Check size={12} />
              </button>
            )}
            <span className="studio-row-swatch" aria-hidden="true" />
            {renderName(segment, `Rename ${segmentDisplayTitle}`, segmentTimelineId, segmentDisplayTitle, true)}
            {isDisabled && renderShowHiddenButton(segment.id, segmentDisplayTitle)}
          </span>
          <span className="studio-row-metric">{formatTime(getSegmentDuration(segment))}</span>
        </span>
        <span className="studio-row-loop">
          {isPlaying && <LoopGlyph color={interactiveColor} />}
        </span>
      </div>
    );
  };

  const renderComboRow = (combo: Segment, index: number) => {
    const range = getComboRange(segments, combo);
    const color = COMBO_SEGMENT_COLOR;
    const isActive = activeComboId === combo.id || activeSegment === combo.id;
    const isSelected = Boolean(combo.isLearned);
    const isPlaying = isLooping && isActive;
    const comboTimelineId = getComboTimelineId(range);
    const comboNumberLabel = comboTimelineId || `C${index + 1}`;
    const comboDisplayTitle = getComboDisplayTitle(combo, comboTimelineId);
    const interactiveColor = getInteractiveSegmentColor(color);
    const rowAction = () => {
      if (selectMode) {
        onToggleSegmentLearned(combo.id);
        return;
      }
      if (!combo.isDisabled) {
        onSelectCombo(combo.id);
      }
    };

    return (
      <div
        key={combo.id}
        ref={element => {
          segmentRowRefs.current[combo.id] = element;
        }}
        role="button"
        tabIndex={combo.isDisabled && !selectMode ? -1 : 0}
        data-testid={`studio-combo-row-${combo.id}`}
        className={[
          'studio-row',
          isActive ? 'is-active' : '',
          isSelected ? 'is-selected' : '',
          combo.isDisabled ? 'is-disabled' : '',
          isPlaying ? 'is-playing' : ''
        ].filter(Boolean).join(' ')}
        style={getStudioRowColorStyle(color)}
        onClick={rowAction}
        onKeyDown={event => handleRowKeyDown(event, rowAction, Boolean(combo.isDisabled) && !selectMode)}
        title={comboDisplayTitle}
      >
        {renderThumbnail({ ...combo, color }, comboNumberLabel)}
        <span className="studio-row-copy">
          <span className="studio-row-main">
            {selectMode && (
              <button
                type="button"
                className={`studio-row-check ${isSelected ? 'is-selected' : ''}`}
                onClick={event => {
                  event.stopPropagation();
                  onToggleSegmentLearned(combo.id);
                }}
                aria-label={isSelected ? `Deselect ${comboDisplayTitle}` : `Select ${comboDisplayTitle}`}
              >
                <Check size={12} />
              </button>
            )}
            <span className="studio-row-swatch" aria-hidden="true" />
            {renderName(combo, `Rename ${comboDisplayTitle}`, comboNumberLabel, comboDisplayTitle, true)}
            {combo.isDisabled && renderShowHiddenButton(combo.id, comboDisplayTitle)}
          </span>
          <span className="studio-row-metric">
            {formatTime(range?.duration ?? getSegmentDuration(combo))}
          </span>
        </span>
        <span className="studio-row-loop">
          {isPlaying && <LoopGlyph color={interactiveColor} combo />}
        </span>
      </div>
    );
  };

  if (segments.length === 0) {
    return null;
  }

  const modeControls = headerControl ? (
    <div
      className={[
        'studio-list-mode-controls',
        variant === 'fullscreen'
          ? 'studio-list-mode-controls-fullscreen'
          : 'studio-list-mode-controls-windowed'
      ].join(' ')}
    >
      {headerControl}
    </div>
  ) : null;

  return (
    <section
      className={[
        'studio-list',
        variant === 'fullscreen' ? 'studio-list-fullscreen' : '',
        className
      ].filter(Boolean).join(' ')}
      aria-label="Studio"
    >
      {showTabs && (
        <div className="studio-list-tabs" role="tablist" aria-label="Studio lists">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'segments'}
            className={`studio-list-tab ${activeTab === 'segments' ? 'is-active' : ''}`}
            onClick={() => onActiveTabChange('segments')}
          >
            <List size={15} />
            Segments
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'combos'}
            className={`studio-list-tab ${activeTab === 'combos' ? 'is-active' : ''}`}
            onClick={() => onActiveTabChange('combos')}
          >
            <Layers size={15} />
            Combos
          </button>
        </div>
      )}

      {variant !== 'fullscreen' && modeControls}

      <div className="studio-list-header">
        <div className="studio-list-heading-slot">
          <h2 className="studio-list-title">{activeTab === 'segments' ? 'Segments' : 'Combos'}</h2>
        </div>
        {activeTab === 'segments' ? (
          <div className="studio-list-actions">
            <button
              type="button"
              className="studio-list-icon-button"
              onClick={scrollToCurrentRow}
              disabled={!canScrollToCurrent}
              aria-label="Scroll to current segment"
              title={canScrollToCurrent ? 'Scroll to current segment' : 'No active segment'}
            >
              <Search size={15} />
            </button>
            <button
              type="button"
              className="studio-list-icon-button"
              onClick={() => activeSourceSegment && onToggleSegmentDisabled(activeSourceSegment.id)}
              disabled={!activeSourceSegment}
              aria-label={activeSourceSegment?.isDisabled ? 'Show selected segment' : 'Hide selected segment'}
              title={activeSourceSegment?.isDisabled ? 'Show selected segment' : 'Hide selected segment'}
            >
              {activeSourceSegment?.isDisabled ? <Eye size={15} /> : <EyeOff size={15} />}
            </button>
            <button
              type="button"
              className="studio-list-icon-button"
              onClick={() => activeSourceSegment && onStartEditingSegment(activeSourceSegment.id)}
              disabled={!activeSourceSegment}
              aria-label="Rename selected segment"
              title="Rename selected segment"
            >
              <Edit size={15} />
            </button>
            <button
              type="button"
              className="studio-list-icon-button is-danger"
              onClick={() => activeSourceSegment && confirmDeleteSegment(activeSourceSegment)}
              disabled={!activeSourceSegment}
              aria-label="Delete selected segment"
              title="Delete selected segment"
            >
              <Trash2 size={15} />
            </button>
            <button
              type="button"
              className={`studio-list-icon-button ${selectMode ? 'is-active' : ''}`}
              onClick={() => setSelectMode(prev => !prev)}
              aria-label={selectMode ? 'Close bulk select' : 'Bulk select segments'}
              title={selectMode ? 'Close bulk select' : 'Bulk select segments'}
            >
              <MoreVertical size={16} />
            </button>
          </div>
        ) : (
          <div className="studio-list-actions">
            <button
              type="button"
              className="studio-list-icon-button"
              onClick={scrollToCurrentRow}
              disabled={!canScrollToCurrent}
              aria-label="Scroll to current combo"
              title={canScrollToCurrent ? 'Scroll to current combo' : 'No active combo'}
            >
              <Search size={15} />
            </button>
            <button
              type="button"
              className="studio-list-icon-button"
              onClick={() => activeCombo && onToggleSegmentDisabled(activeCombo.id)}
              disabled={!activeCombo}
              aria-label={activeCombo?.isDisabled ? 'Show selected combo' : 'Hide selected combo'}
              title={activeCombo?.isDisabled ? 'Show selected combo' : 'Hide selected combo'}
            >
              {activeCombo?.isDisabled ? <Eye size={15} /> : <EyeOff size={15} />}
            </button>
            <button
              type="button"
              className="studio-list-icon-button"
              onClick={() => activeCombo && onStartEditingSegment(activeCombo.id)}
              disabled={!activeCombo}
              aria-label="Rename selected combo"
              title="Rename selected combo"
            >
              <Edit size={15} />
            </button>
            <button
              type="button"
              className="studio-list-icon-button"
              onClick={() => activeCombo && onDuplicateCombo(activeCombo.id)}
              disabled={!activeCombo}
              aria-label="Duplicate selected combo"
              title="Duplicate selected combo"
            >
              <Copy size={15} />
            </button>
            <button
              type="button"
              className="studio-list-icon-button is-danger"
              onClick={() => activeCombo && confirmDeleteCombo(activeCombo)}
              disabled={!activeCombo}
              aria-label="Delete selected combo"
              title="Delete selected combo"
            >
              <Trash2 size={15} />
            </button>
            <button
              type="button"
              className="studio-list-icon-button"
              onClick={onCreateCombo}
              disabled={sourceSegments.length === 0}
              aria-label="Create combo"
              title="Create combo from current segment"
            >
              <Plus size={16} />
            </button>
            <button
              type="button"
              className={`studio-list-icon-button ${selectMode ? 'is-active' : ''}`}
              onClick={() => setSelectMode(prev => !prev)}
              aria-label={selectMode ? 'Close bulk select' : 'Bulk select combos'}
              title={selectMode ? 'Close bulk select' : 'Bulk select combos'}
            >
              <MoreVertical size={16} />
            </button>
          </div>
        )}
      </div>

      {selectMode && (
        <div className="studio-list-bulkbar">
          <span className="studio-list-bulkcount">{selectedCount} selected</span>
          {onToggleAllLearned && (
            <button
              type="button"
              className="studio-list-bulk-button"
              onClick={() => onToggleAllLearned(!hasSelectedRows, activeTab)}
            >
              {hasSelectedRows ? <Square size={13} /> : <CheckSquare size={13} />}
              {hasSelectedRows ? 'Clear' : 'All'}
            </button>
          )}
          <button
            type="button"
            className="studio-list-bulk-button"
            onClick={() => onBatchToggleDisabledSelected(activeTab)}
            disabled={selectedCount === 0}
            title={hasEnabledSelectedRows ? `Hide selected ${tabNounPlural}` : `Show selected ${tabNounPlural}`}
          >
            {hasEnabledSelectedRows ? <EyeOff size={13} /> : <Eye size={13} />}
            {hasEnabledSelectedRows ? 'Hide' : 'Show'}
          </button>
          <button
            type="button"
            className="studio-list-bulk-button is-danger"
            onClick={confirmBatchDelete}
            disabled={selectedCount === 0}
          >
            <Trash2 size={13} />
            Delete
          </button>
        </div>
      )}

      <div ref={listContainerRef} className="studio-list-rows">
        {tabRows.length === 0 && (
          <div className="studio-list-empty">
            {activeTab === 'segments' ? 'No segments yet.' : 'No combos yet.'}
          </div>
        )}
        {activeTab === 'segments'
          ? sourceSegments.map(renderSegmentRow)
          : comboSegments.map(renderComboRow)}
      </div>

      {variant === 'fullscreen' && modeControls}

      {editingRow && (
        <SegmentRenameDialog
          key={editingRow.id}
          segment={editingRow}
          inputRef={segmentNameInputRef}
          onSave={name => onSaveSegmentName(editingRow.id, name)}
          onCancel={() => (
            onCancelEditingSegment
              ? onCancelEditingSegment()
              : onSaveSegmentName(editingRow.id, editingRow.name)
          )}
        />
      )}
    </section>
  );
};

/** Rename dialog for one segment or combo; keeps its own draft until OK or Cancel. */
const SegmentRenameDialog: React.FC<{
  segment: Segment;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onSave: (name: string) => void;
  onCancel: () => void;
}> = ({ segment, inputRef, onSave, onCancel }) => {
  const [draft, setDraft] = useState(segment.name);
  const noun = isCombo(segment) ? 'combo' : 'segment';

  return (
    <RenameDialog
      title={`Rename ${noun}`}
      value={draft}
      onChange={setDraft}
      onConfirm={() => onSave(draft.trim())}
      onCancel={onCancel}
      inputLabel={`Rename ${noun} ${segment.name}`}
      confirmLabel={`Save ${noun} name`}
      inputRef={inputRef}
    />
  );
};

export default StudioList;
